import React, { useState, useRef, useEffect } from "react";

// ─── Types ───────────────────────────────────────────────────────────────────
interface FaqItem {
    question: string;
    answer: string;
}

interface GeneralTerm {
    name: string;
    answer: string;
}

interface Service {
    title: string;
    covered: string;
    notCovered: string;
}

interface ServiceCategory {
    category: string;
    services: Service[];
}

interface ImageGroup {
    before: string[];
    after: string[];
}

interface ModalData {
    images: string[];
    type: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const faqItems: FaqItem[] = [
    {
        question: "What is Care+ and how does it work?",
        answer: "Care+ is an optional after-care subscription plan that covers your footwear or handbags for accidental damage, wear & tear, and minor repairs. You can raise a service request anytime during the subscription period, and we'll handle the rest!",
    },
    {
        question: "What types of products are eligible for Care+?",
        answer: "Care+ is currently available for footwear & handbags, purchased through our official channels.",
    },
    {
        question: "How long is my Care+ subscription valid?",
        answer: "Care+ covers your product for up to 24 months from the date of purchase, depending on the plan you select at checkout.",
    },
    {
        question: "How do I raise a Care+ service request?",
        answer: "You can submit a service request via the link in your order purchase confirmation email.",
    },
    {
        question: "How long does the service take?",
        answer: "Once your service request is approved and the item is received, the typical turnaround time is 7–14 business days depending on the service required.",
    },
    {
        question: "Will my product look brand new after the service?",
        answer: "While we do our best to restore your item to its original look, some deep wear or damage may leave minor traces. Our goal is always to bring it as close to 'like-new' as possible.",
    },
    {
        question: "Is Care+ transferable to another person?",
        answer: "No, Care+ is linked to your original purchase and is not transferable. It can only be used by the customer who bought the item and opted in for Care+.",
    },
    {
        question: "How often can I claim the Care+ service?",
        answer: "The Care+ service can be claimed once every four (4) months. Claims exceeding this frequency will not be eligible.",
    },
];

const generalTerms: GeneralTerm[] = [
    {
        name: "Service Eligibility",
        answer: "Care+ is valid only for eligible products registered at the time of purchase or activation. To qualify, items must be - authentically manufactured (no counterfeit or replica items permitted), within the defined subscription period, free from pre-existing structural damage that makes cosmetic restoration unsafe or ineffective and submitted via approved channels, with original purchase details where applicable. We reserve the right to decline service requests based on item condition, age, modifications, or failure to meet eligibility criteria.",
    },
    {
        name: "Service Process",
        answer: "All service requests must be initiated through the official Care+ request portal or email as instructed at the time of purchase. The customer is responsible for safe packaging and shipping of the item unless otherwise specified in writing. Once the item is received, it is subject to inspection and acceptance. Turnaround time may vary based on item condition and service type. Ineligible items, or those beyond repair, may be returned without service. In such cases, no refund of the Care+ fee will be issued.",
    },
    {
        name: "Service Limitations",
        answer: "Care+ is a cosmetic restoration service only. It is not an insurance product or warranty for accidental loss, theft, manufacturing defects, or major structural damage. Services are delivered on a best-effort basis and outcomes may vary depending on material behavior, age of product, and extent of wear or damage. Restoration is limited to what is described under the Scope of Services. Any customization, full repainting, structural rebuilds, or extensive deconstruction are expressly excluded unless specified otherwise in a separate agreement.",
    },
];

const serviceScope: ServiceCategory[] = [
    {
        category: "Sneakers",
        services: [
            {
                title: "De-Yellowing",
                covered:
                    "De-yellowing is a restoration process designed to reduce oxidation stains and yellowing on sneaker components such as translucent or white soles, midsoles, and plastic elements. Using UV-reactive treatments and oxidation-reversing agents, the process works to brighten discolored areas and restore a cleaner, more uniform appearance.",
                notCovered:
                    "On transparent PU, plastic, or rubber materials, this treatment typically lightens yellowing only up to 20–30%, and complete whitening is rarely possible. Deep-set oxidation, long-term aging, or environmental damage may result in permanent discoloration. Yellowing that has seeped into the upper, glue lines, or sneakers with pre-treated or coated surfaces may also be excluded, as results cannot be guaranteed.",
            },
            {
                title: "Stain Removal",
                covered:
                    "Our Stain Removal service targets localized marks from common substances like food, dirt, ink, or water on leather, mesh, and fabric uppers. Using material-safe solvents and precision spot-cleaning techniques, we work to lift visible stains without harming the surface. A light conditioning treatment is applied afterward to help maintain the texture and prevent dryness.",
                notCovered:
                    "This service does not cover stains caused by oils, turmeric, paint, bleach, or dyes, as these often chemically alter the material and cannot be safely reversed. We also do not treat stains on synthetic panels, as the material may degrade further—especially on older stock with limited shelf life. In cases where stain removal could compromise delicate materials (such as untreated suede or fine mesh), the service may be declined.",
            },
            {
                title: "Scuff & Scratch Refinement",
                covered:
                    "This service targets minor scuffs and surface-level scratches on genuine leather, using a combination of fillers, dyes, and finish refinement techniques. It helps improve the appearance of worn or rubbed areas while preserving the original color, feel, and structure of the sneaker.",
                notCovered:
                    "This service does not apply to synthetic leather or materials that may react unpredictably to refinishing. Deep cuts, fabric tears, or damage that has penetrated the upper layers are not eligible. Full panel repainting or custom color refinishing is also outside the scope of this service.",
            },
            {
                title: "Re-Stitching",
                covered:
                    "Re-Stitching under Care+ covers minor thread reinforcements or restorations where original seams have loosened or come undone. This is most effective for high-wear zones such as toe boxes, collars, or heel panels, and is done using color-matched, durable threads.",
                notCovered:
                    "Full sole reattachment, tongue separation, or complete deconstruction and rebuild of the sneaker are not included. We also do not perform cosmetic stitch customization or patchwork outside the sneaker's original design. If stitching damage is due to underlying sole or material failure, the repair may be declined.",
            },
            {
                title: "Crease Reduction",
                covered:
                    "Reduction of surface-level creases on the toe box or upper panels, smoothing out light to moderate fabric or leather wrinkling, and restoration using controlled heat and pressure techniques.",
                notCovered:
                    "Permanent creases caused by material breakdown, deep-set structural folds, torn or stretched fabric, and crease removal on mesh or delicate materials prone to damage.",
            },
            {
                title: "Sole Repair",
                covered:
                    "Sole Repair includes minor cosmetic restorations for worn-out or scuffed soles on vulcanized rubber sole types. Our team addresses light abrasion or fading through material-compatible filling, reshaping, and smoothing. Additionally, minor sole pasting is included — ideal for instances where a small section of the sole begins to detach, such as partial separation at the toe or heel. These treatments are intended to improve the aesthetic and reinforce the longevity of the sole when damage is superficial.",
                notCovered:
                    "Complete re-soling, full heel reconstruction, or any major structural work is excluded under Care+. If the sole's integrity is significantly compromised, or the damage extends beyond superficial wear, the item may be deemed ineligible for service.",
            },
            {
                title: "Edge Restoration",
                covered:
                    "Edge repainting for scuffed or discolored midsoles, minor surface crack filling on rubber or foam edges, restoration of faded or scratched finishes, and standard color matching to the original design.",
                notCovered:
                    "Deep structural cracks affecting sole integrity, full sole separation or replacement, custom color changes or artistic redesigns, and damage caused by chemical exposure or heat deformation.",
            },
        ],
    },
];

const imageGroups: ImageGroup[] = [
    {
        before: ["/image1b1.jpeg", "/image1b2.jpeg", "/image1b3.jpeg"],
        after: ["/image1a1.jpeg", "/image1a2.jpeg", "/image1a3.jpeg"],
    },
    {
        before: ["/image2b1.jpeg", "/image2b2.jpeg", "/image2b3.jpeg"],
        after: ["/image2a1.jpeg", "/image2a2.jpeg", "/image2a3.jpeg"],
    },
    {
        before: ["/image3b1.jpeg", "/image3b2.jpeg", "/image3b3.jpeg"],
        after: ["/image3a1.jpeg", "/image3a2.jpeg", "/image3a3.jpeg"],
    },
];

const navItems = [
    { label: "Frequently Asked Questions", id: "faq" },
    { label: "Scope of Services", id: "scope" },
    { label: "Our Prior Work", id: "prior-work" },
];

// ─── Modal Component ──────────────────────────────────────────────────────────
interface ImageModalProps {
    images: string[];
    type: string;
    onClose: () => void;
}

function ImageModal({ images, type, onClose }: ImageModalProps) {
    const [currentIndex, setCurrentIndex] = useState<number>(0);

    if (!images || images.length === 0) return null;

    const handlePrev = (): void => {
        setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNext = (): void => {
        setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
            onClick={onClose}
        >
            <div
                className="relative max-w-2xl w-full mx-4 p-4 rounded-xl"
                style={{ backgroundColor: "#1e3a5f" }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    aria-label="Close modal"
                    className="absolute top-3 right-4 text-white text-2xl font-bold hover:opacity-70 transition z-10"
                >
                    ✕
                </button>

                <div className="flex justify-start mb-4">
                    <span
                        className="px-4 py-1 rounded-full text-sm font-semibold"
                        style={{
                            backgroundColor: type === "Before" ? "#1e40af" : "#dbeafe",
                            color: type === "Before" ? "#ffffff" : "#1e40af",
                            border: "1px solid #3b82f6",
                        }}
                    >
                        {type}
                    </span>
                </div>

                <div className="flex items-center justify-center mb-4">
                    <button
                        onClick={handlePrev}
                        aria-label="Previous image"
                        className="text-white text-3xl font-bold px-3 hover:opacity-70 transition select-none"
                    >
                        ‹
                    </button>
                    <img
                        src={images[currentIndex]}
                        alt={`${type} ${currentIndex + 1}`}
                        className="max-h-96 max-w-full object-contain rounded-lg"
                    />
                    <button
                        onClick={handleNext}
                        aria-label="Next image"
                        className="text-white text-3xl font-bold px-3 hover:opacity-70 transition select-none"
                    >
                        ›
                    </button>
                </div>

                <div className="flex justify-center gap-3">
                    {images.map((img: string, idx: number) => (
                        <img
                            key={idx}
                            src={img}
                            alt={`${type} thumb ${idx + 1}`}
                            className="w-16 h-16 object-cover rounded cursor-pointer transition"
                            style={{
                                border: currentIndex === idx ? "2px solid #3b82f6" : "2px solid #93c5fd",
                                opacity: currentIndex === idx ? 1 : 0.5,
                            }}
                            onClick={() => setCurrentIndex(idx)}
                        />
                    ))}
                </div>

                <p className="text-center text-blue-200 text-sm mt-3">
                    {currentIndex + 1} / {images.length}
                </p>
            </div>
        </div>
    );
}

// ─── Navbar Component ─────────────────────────────────────────────────────────
interface NavbarProps {
    onNavigate: (id: string) => void;
}

function Navbar({ onNavigate }: NavbarProps) {
    const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

    const handleClick = (id: string): void => {
        setMobileMenuOpen(false);
        // Small delay to let menu close first, then scroll
        setTimeout(() => {
            onNavigate(id);
        }, 100);
    };

    return (
        <nav className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm" id="main-navbar">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
                {/* Desktop */}
                <div className="hidden md:flex items-center justify-center h-14 gap-10">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => handleClick(item.id)}
                            className="text-sm font-medium text-gray-600 hover:text-blue-700 transition-colors duration-200"
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                {/* Mobile */}
                <div className="flex md:hidden items-center justify-end h-14">
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle menu"
                        className="flex flex-col justify-center items-center w-8 h-8 gap-1.5"
                    >
                        <span
                            className={`block w-6 h-0.5 bg-gray-700 transition-all duration-300 ${
                                mobileMenuOpen ? "rotate-45 translate-y-2" : ""
                            }`}
                        />
                        <span
                            className={`block w-6 h-0.5 bg-gray-700 transition-all duration-300 ${
                                mobileMenuOpen ? "opacity-0" : ""
                            }`}
                        />
                        <span
                            className={`block w-6 h-0.5 bg-gray-700 transition-all duration-300 ${
                                mobileMenuOpen ? "-rotate-45 -translate-y-2" : ""
                            }`}
                        />
                    </button>
                </div>

                {/* Mobile Dropdown */}
                <div
                    className={`md:hidden overflow-hidden transition-all duration-300 ${
                        mobileMenuOpen ? "max-h-60 pb-4" : "max-h-0"
                    }`}
                >
                    <div className="border-t border-gray-100">
                        {navItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => handleClick(item.id)}
                                className="block w-full text-left px-2 py-3 text-sm font-medium text-gray-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors duration-200"
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </nav>
    );
}

// ─── Main Page Component ──────────────────────────────────────────────────────
export default function TermsPage() {
    const [activeTab, setActiveTab] = useState<string>("Sneakers");
    const [modalData, setModalData] = useState<ModalData | null>(null);

    const faqRef = useRef<HTMLDivElement>(null);
    const scopeRef = useRef<HTMLDivElement>(null);
    const priorWorkRef = useRef<HTMLDivElement>(null);
    const navbarRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        navbarRef.current = document.getElementById("main-navbar");
    }, []);

    const openModal = (images: string[], type: string): void => {
        setModalData({ images, type });
    };

    const closeModal = (): void => {
        setModalData(null);
    };

    const scrollToSection = (id: string): void => {
        const refMap: Record<string, React.RefObject<HTMLDivElement>> = {
            faq: faqRef,
            scope: scopeRef,
            "prior-work": priorWorkRef,
        };
        const ref = refMap[id];
        if (ref?.current) {
            const navbarHeight = navbarRef.current?.offsetHeight || 0;
            const elementTop = ref.current.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementTop - navbarHeight - 16; // 16px extra padding

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth",
            });
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 to-black">
            {/* Navbar */}
            <Navbar onNavigate={scrollToSection} />

            {/* Page Content */}
            <div className="flex items-center justify-center p-6">
                <div className="bg-white p-6 sm:p-10 rounded-lg shadow-lg w-full max-w-4xl text-gray-800">
                    <h1 className="text-3xl font-bold text-center mb-6 text-blue-700">Care+ Terms & Conditions</h1>

                    {/* General Coverage */}
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold text-blue-600">General Coverage</h2>
                        <p className="mt-2 text-sm text-gray-700">
                            Care+ offers comprehensive after-care subscription for your footwear and handbags, covering
                            common issues like accidental scuffs, surface scratches, loose stitching, and normal wear &
                            tear from regular use. Our service includes professional cleaning, minor repairs (such as
                            sole separation, zipper fixes, or re-stitching), and light refurbishment to keep your items
                            in top condition. Whether it's a faded sneaker or a slightly damaged handbag, Care+ ensures
                            your product longevity.
                        </p>
                    </div>

                    {/* FAQ Section */}
                    <div className="mb-8" ref={faqRef} id="faq">
                        <h2 className="text-xl font-semibold text-blue-600 mb-4">Frequently Asked Questions</h2>
                        <div className="space-y-6">
                            {faqItems.map((faq: FaqItem, index: number) => (
                                <div
                                    key={index}
                                    className="p-5 border border-gray-200 rounded-xl bg-gradient-to-r from-blue-50 via-white to-blue-50 shadow-md hover:shadow-lg transition"
                                >
                                    <h3 className="text-md font-semibold text-blue-800 mb-1">{faq.question}</h3>
                                    <p className="text-sm text-gray-700">{faq.answer}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Scope of Services Section */}
                    <div className="mb-8" ref={scopeRef} id="scope">
                        <h2 className="text-xl font-semibold text-blue-600 mb-4">Scope of Services</h2>
                        <p className="mt-2 text-sm text-gray-700">
                            Care+ provides category-specific after-care solutions to ensure your product receives the
                            most appropriate treatment based on its material, construction, and usage. To help you
                            better understand the services applicable to your purchase, we have segmented our offerings
                            into key categories. Please view the full scope of services available—ranging from cleaning
                            and conditioning to structural repairs and refurbishments—tailored to preserve the quality
                            and longevity of your item.
                        </p>

                        {/* Service Tabs */}
                        <div className="max-w-3xl mx-auto mt-6">
                            <div className="flex w-full border border-gray-200 rounded-t-md overflow-hidden shadow-sm">
                                {serviceScope.map((cat: ServiceCategory) => (
                                    <button
                                        key={cat.category}
                                        className={`flex-1 py-3 text-l font-medium transition-all duration-200 text-center ${
                                            activeTab === cat.category
                                                ? "bg-blue-100 text-blue-700"
                                                : "bg-white text-gray-600 hover:bg-gray-50"
                                        }`}
                                        onClick={() => setActiveTab(cat.category)}
                                    >
                                        {cat.category}
                                    </button>
                                ))}
                            </div>

                            {serviceScope
                                .filter((cat: ServiceCategory) => cat.category === activeTab)
                                .map((section: ServiceCategory, idx: number) => (
                                    <div
                                        key={idx}
                                        className="space-y-6 bg-gray-50 p-6 border border-t-0 border-gray-200 rounded-b-md shadow-sm"
                                    >
                                        {section.services.map((service: Service, i: number) => (
                                            <div key={i} className="p-2">
                                                <h3 className="text-base font-bold text-blue-400 mb-1 text-center">
                                                    {service.title}
                                                </h3>
                                                <p className="text-sm text-gray-700 mb-2">
                                                    <span className="italic">
                                                        <b>What's Covered:</b>
                                                    </span>
                                                    <br />
                                                    <span className="mt-2">{service.covered}</span>
                                                </p>
                                                <p className="text-sm text-gray-700">
                                                    <span className="italic">
                                                        <b>What's Not Covered:</b>
                                                    </span>
                                                    <br />
                                                    <span className="mt-2">{service.notCovered}</span>
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                        </div>
                    </div>

                    {/* General Terms */}
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold text-blue-600 mb-4">General Terms & Conditions</h2>
                        <div className="space-y-6">
                            {generalTerms.map((term: GeneralTerm, index: number) => (
                                <div
                                    key={index}
                                    className="p-5 border border-gray-200 rounded-xl bg-gradient-to-r from-blue-50 via-white to-blue-50 shadow-md hover:shadow-lg transition"
                                >
                                    <h3 className="text-md font-semibold text-blue-800 mb-1">{term.name}</h3>
                                    <p className="text-sm text-gray-700">{term.answer}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Our Prior Work Section */}
                    <div ref={priorWorkRef} id="prior-work" className="mb-8">
                        <h2 className="text-xl font-semibold text-blue-600 mb-4">Our Prior Work</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {imageGroups.map((group: ImageGroup, index: number) => (
                                <div
                                    key={index}
                                    className="rounded-xl overflow-hidden shadow-md bg-white border border-gray-200"
                                >
                                    {/* BEFORE */}
                                    <div
                                        className="relative cursor-pointer overflow-hidden"
                                        onClick={() => openModal(group.before, "Before")}
                                    >
                                        <span
                                            className="absolute left-3 top-3 z-10 pointer-events-none
                                                inline-flex items-center justify-center
                                                w-24 h-8 rounded-full
                                                text-sm font-bold text-center"
                                            style={{
                                                backgroundColor: "#1e40af",
                                                color: "#ffffff",
                                                border: "1px solid #3b82f6",
                                                backdropFilter: "blur(2px)",
                                            }}
                                        >
                                            Before
                                        </span>
                                        <img
                                            src={group.before[0]}
                                            alt={`Before ${index + 1}`}
                                            className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                                        />
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-gray-200" />

                                    {/* AFTER */}
                                    <div
                                        className="relative cursor-pointer overflow-hidden"
                                        onClick={() => openModal(group.after, "After")}
                                    >
                                        <span
                                            className="absolute left-3 top-3 z-10 pointer-events-none
                                                inline-flex items-center justify-center
                                                w-24 h-8 rounded-full
                                                text-sm font-bold text-center"
                                            style={{
                                                backgroundColor: "#dbeafe",
                                                color: "#1e40af",
                                                border: "1px solid #3b82f6",
                                                backdropFilter: "blur(2px)",
                                            }}
                                        >
                                            After
                                        </span>
                                        <img
                                            src={group.after[0]}
                                            alt={`After ${index + 1}`}
                                            className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Privacy */}
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold text-blue-600 mb-4">Privacy & Data Use</h2>
                        <p className="mt-2 text-sm text-gray-700">
                            By submitting an item for Care+ service, you consent to the collection and processing of
                            your personal data in accordance with our Privacy Policy. This may include your name,
                            contact information, item details, and transaction history. Your data will only be used to
                            process your service and improve our offerings.
                        </p>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {modalData && <ImageModal images={modalData.images} type={modalData.type} onClose={closeModal} />}
        </div>
    );
}
